import React, { useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import jalocale from "@fullcalendar/core/locales/ja";

import Events from './Event';
import { CiCalendar } from 'react-icons/ci';
import { IoMdLink } from 'react-icons/io';
import { FaExternalLinkAlt } from 'react-icons/fa';

import "./EventLists.css";

// リンク情報の表示用テキストと遷移先URLを解決するヘルパー
const getLinkInfo = (link: string) => {
	if (!link) return { text: "", href: "" };

	// プロトコルから始まらない場合はテキスト（建物名/住所）とみなし、検索リンクを生成
	if (!/^https?:\/\//i.test(link)) {
		return {
			text: link,
			href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(link)}`
		};
	}

	try {
		const url = new URL(link);
		// Google Maps URL (place または search パターン) の解析
		if (url.hostname.includes("google.com")) {
			const pathParts = url.pathname.split('/');
			const targetIdx = pathParts.findIndex(p => p === 'place' || p === 'search');
			
			const targetPart = pathParts[targetIdx + 1];
			if (targetIdx !== -1 && targetPart) {
				// @より前の部分を取得し、デコード。+をスペースに戻す。
				const name = decodeURIComponent(targetPart.split('@')[0]?.replace(/\+/g, ' ') || "");
				return { text: name, href: link };
			}
			const q = url.searchParams.get('q');
			if (q) return { text: q, href: link };
		}
	} catch (e) { /* パース失敗時はそのまま返す */ }

	return { text: link, href: link };
};

// description内のURLをリンクに変換し、アイコンを添えるヘルパー
const renderDescriptionWithLinks = (text: string) => {
	if (!text) return null;
	const urlRegex = /(https?:\/\/[^\s]+)/gi;
	const parts = text.split(urlRegex);

	return parts.map((part, i) => {
		if (part && /^https?:\/\//i.test(part)) {
			return (
				<a
					key={i}
					href={part}
					target="_blank"
					rel="noopener noreferrer"
					onClick={(e) => e.stopPropagation()}
					className="description-link"
				>
					{part}
					<FaExternalLinkAlt size={12} className="external-icon" />
				</a>
			);
		}
		return part;
	});
};

function EventLists(){
	const [selectedEvent, setSelectedEvent] = useState<any>(null);

	// イベントクリック時の処理
	const handleEventClick = (info: any) => {
		setSelectedEvent({
			title: info.event.title,
			start: info.event.start,
			end: info.event.end,
			description: info.event.extendedProps.description,
			link: info.event.extendedProps.link,
		});
	};

	// Googleカレンダー登録用URL生成
	const getGoogleCalendarUrl = (event: any) => {
		if (!event.start) return "";
		const formatDate = (date: Date) => {
			try {
				return date.toISOString().replace(/-|:|\.\d+/g, '');
			} catch (e) { return ""; }
		};
		const start = formatDate(new Date(event.start));
		// 終了時間がない場合は開始時間の1時間後に設定（Googleカレンダーの仕様に合わせる）
		const end = formatDate(event.end ? new Date(event.end) : new Date(new Date(event.start).getTime() + 3600000));
		
		return `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title)}&dates=${start}/${end}&details=${encodeURIComponent(event.description || '')}&location=${encodeURIComponent(event.link || '')}`;
	};

	return(
		<div style={{ position: 'relative' }}>
			<FullCalendar
				plugins={[dayGridPlugin,timeGridPlugin,listPlugin]}
				initialView='listHalfYear'
				locales={[jalocale]}
				locale="ja"

				displayEventTime={true}
    			displayEventEnd={true} 

				buttonText={{
					today: '現在'
				}}

				views={{
					dayGridMonth: {
						displayEventTime: false,
						eventDisplay: 'block'
					},
					listHalfYear:{
						type:'list',
						duration:{months:7},
						buttonText:'予定'
					},
					listWeek: {
						buttonText: '週'
					}
				}}

				headerToolbar={{
					left:'title',
					center:'dayGridMonth,listWeek,listHalfYear',
					right:'prev,today,next',
				}}

				eventClassNames="clickable-event"
				eventClick={handleEventClick}
				events={Events}
			
			/>

			{/* イベント詳細ポップアップ */}
			{createPortal(
				<AnimatePresence>
					{selectedEvent && (
						<motion.div 
							className="event-modal-overlay"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							onClick={() => setSelectedEvent(null)}
						>
							<motion.div 
								className="event-modal-content"
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0, y: 20 }}
								onClick={(e) => e.stopPropagation()}
							>
								<h2 className="event-modal-title">{selectedEvent.title}</h2>
								
								{selectedEvent.link && (() => {
									const linkInfo = getLinkInfo(selectedEvent.link);
									return (
									<div className="event-modal-external-link">
										<a 
											href={linkInfo.href} 
											target="_blank" 
											rel="noopener noreferrer"
											onClick={(e) => e.stopPropagation()}
										>
											<IoMdLink style={{ flexShrink: 0 }} size={18} />
											<span>{linkInfo.text}</span>
										</a>
									</div>
									);
								})()}

								<p className="event-modal-date">
									{new Date(selectedEvent.start).toLocaleDateString('ja-JP', {
										year: 'numeric',
										month: 'long',
										day: 'numeric'
									})}
								</p>
								
								<div className="event-modal-description">
									{renderDescriptionWithLinks(selectedEvent.description)}
								</div>
								
								
								<a 
									href={getGoogleCalendarUrl(selectedEvent)}
									target="_blank"
									rel="noopener noreferrer"
									className="google-calendar-link"
									title="Googleカレンダーに予定を追加"
								>
									<CiCalendar size={32} />
								</a>

							</motion.div>
						</motion.div>
					)}
				</AnimatePresence>,
				document.body
			)}
		</div>
	)
}

export default EventLists;